import { Controller } from '@hotwired/stimulus'

// Read-only map of a service area's delivery zone plus the same owner's
// other service areas (muted + labeled) for boundary coordination.
export default class extends Controller {
  static targets = ['canvas']
  static values = {
    token: String,
    polygon: Object,
    label: String,
    subjectId: Number,
    siblingsUrl: String,
    fallbackCenter: Object,
  }

  async connect() {
    // Dynamic import keeps mapbox-gl out of the shared vendors chunk
    this.mapboxgl = (await import('mapbox-gl')).default
    this.mapboxgl.accessToken = this.tokenValue
    this.siblingFeatures = []

    this.map = new this.mapboxgl.Map({
      container: this.canvasTarget,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: this.initialCenter(),
      zoom: 9,
    })
    this.map.addControl(new this.mapboxgl.NavigationControl())

    this.map.on('load', async () => {
      await this.renderSubject() // async in the map-draw subclass
      await this.loadSiblings()
      this.fitToContents()
    })
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
    }
  }

  hasSubjectPolygon() {
    return this.polygonValue && this.polygonValue.type === 'Polygon'
  }

  subjectFeature() {
    return {
      type: 'Feature',
      properties: { label: this.labelValue },
      geometry: this.polygonValue,
    }
  }

  renderSubject() {
    if (!this.hasSubjectPolygon()) {
      return
    }

    this.map.addSource('subject', { type: 'geojson', data: this.subjectFeature() })
    this.map.addLayer({
      id: 'subject-fill',
      type: 'fill',
      source: 'subject',
      paint: { 'fill-color': '#5b21b6', 'fill-opacity': 0.25 },
    })
    this.map.addLayer({
      id: 'subject-line',
      type: 'line',
      source: 'subject',
      paint: { 'line-color': '#5b21b6', 'line-width': 2 },
    })
  }

  async loadSiblings() {
    if (!this.siblingsUrlValue) {
      return
    }

    const areas = await this.fetchSiblingAreas()
    if (!areas) {
      return // siblings are context, not critical — render the subject alone
    }

    const siblings = areas.filter(area => area.id !== this.subjectIdValue && area.geog)
    if (siblings.length === 0) {
      return
    }

    this.siblingFeatures = siblings.map(area => ({
      type: 'Feature',
      properties: { label: area.label },
      geometry: area.geog,
    }))
    const labelPoints = siblings
      .filter(area => area.center)
      .map(area => ({
        type: 'Feature',
        properties: { label: area.label },
        geometry: area.center,
      }))

    this.map.addSource('siblings', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: this.siblingFeatures },
    })
    this.map.addSource('sibling-labels', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: labelPoints },
    })
    this.map.addLayer({
      id: 'siblings-fill',
      type: 'fill',
      source: 'siblings',
      paint: { 'fill-color': '#6b7280', 'fill-opacity': 0.15 },
    })
    this.map.addLayer({
      id: 'siblings-line',
      type: 'line',
      source: 'siblings',
      paint: { 'line-color': '#6b7280', 'line-width': 2, 'line-dasharray': [2, 2] },
    })
    this.map.addLayer({
      id: 'sibling-labels',
      type: 'symbol',
      source: 'sibling-labels',
      layout: { 'text-field': ['get', 'label'], 'text-size': 14 },
      paint: { 'text-color': '#374151', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 },
    })
  }

  async fetchSiblingAreas() {
    try {
      const response = await fetch(this.siblingsUrlValue, { headers: { Accept: 'application/json' } })
      if (!response.ok) {
        return null
      }
      return await response.json()
    } catch {
      return null
    }
  }

  fitToContents() {
    const bounds = new this.mapboxgl.LngLatBounds()
    this.eachRenderedCoordinate(coordinate => bounds.extend(coordinate))

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, { padding: 48, animate: false, maxZoom: 14 })
    } else if (this.hasFallbackCenter()) {
      this.map.jumpTo({ center: this.fallbackCenterCoordinate(), zoom: 11 })
    }
  }

  eachRenderedCoordinate(callback) {
    const features = [...this.siblingFeatures]
    if (this.hasSubjectPolygon()) {
      features.push(this.subjectFeature())
    }

    features.forEach(feature => {
      feature.geometry.coordinates.forEach(ring => {
        ring.forEach(coordinate => callback(coordinate))
      })
    })
  }

  hasFallbackCenter() {
    return this.fallbackCenterValue && this.fallbackCenterValue.lat && this.fallbackCenterValue.lng
  }

  fallbackCenterCoordinate() {
    return [this.fallbackCenterValue.lng, this.fallbackCenterValue.lat]
  }

  initialCenter() {
    if (this.hasFallbackCenter()) {
      return this.fallbackCenterCoordinate()
    }

    return [-86.0, 30.3] // gulf coast default until fitToContents runs
  }
}
